import React, { useState, useMemo, useCallback } from 'react';
import { useApiMode } from '../context/ApiModeContext';
import './ApiExplorer.css';

const CopyIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
  </svg>
);

const API_TABS = [
  { id: 'create', label: 'Create' },
  { id: 'update', label: 'Update' },
  { id: 'get-customers', label: 'Get Customers' },
  { id: 'get-customer', label: 'Get Customer' },
];

const getDefaultCreateFields = (partners) => {
  const firstPartnerId = partners.length ? String(partners[0].id) : '';
  return {
    partner_id: firstPartnerId,
    name: '',
    email: '',
    phone: '',
    country: 'US',
    kyc_required: true,
  };
};

const getDefaultUpdateFields = (partners) => {
  const firstPartnerId = partners.length ? String(partners[0].id) : '';
  return {
    partner_id: firstPartnerId,
    customer_id: 'CZE000001',
    name: '',
    email: '',
    phone: '',
    country: 'US',
    kyc_required: true,
  };
};

const ApiExplorer = ({
  baseUrl: baseUrlProp,
  partners = [],
  onClose,
  onLogEntry,
}) => {
  const { apiBaseUrl: contextBaseUrl } = useApiMode();
  const baseUrl = baseUrlProp || contextBaseUrl;
  const [activeTab, setActiveTab] = useState('create');
  const [createJson, setCreateJson] = useState(() =>
    JSON.stringify(getDefaultCreateFields(partners), null, 2)
  );
  const [updateJson, setUpdateJson] = useState(() =>
    JSON.stringify(getDefaultUpdateFields(partners), null, 2)
  );
  const [getCustomersPartnerId, setGetCustomersPartnerId] = useState(partners[0] ? String(partners[0].id) : '');
  const [getCustomerId, setGetCustomerId] = useState('CZE000001');
  const [responseText, setResponseText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const base = baseUrl.replace(/\/$/, '');

  const selectedPartnerIdFromJson = useMemo(() => {
    try {
      const o = JSON.parse(createJson);
      return o.partner_id != null ? String(o.partner_id) : (partners[0] && String(partners[0].id));
    } catch {
      return partners[0] ? String(partners[0].id) : '';
    }
  }, [createJson, partners]);

  const updateCustomerId = useMemo(() => {
    try {
      const u = JSON.parse(updateJson);
      return (u && u.customer_id) ? String(u.customer_id) : 'CZE000001';
    } catch {
      return 'CZE000001';
    }
  }, [updateJson]);

  const tabConfig = useMemo(() => {
    const partnerId = getCustomersPartnerId || (partners[0] && partners[0].id);
    return {
      create: {
        method: 'POST',
        endpoint: `${base}/api/v1/customers`,
        fullUrl: `${base}/api/v1/customers`,
      },
      update: {
        method: 'PUT',
        endpoint: `${base}/api/v1/customers/{customer_id}`,
        fullUrl: `${base}/api/v1/customers/${updateCustomerId}`,
      },
      'get-customers': {
        method: 'GET',
        endpoint: `${base}/api/v1/customers?partner_id={partner_id}`,
        fullUrl: `${base}/api/v1/customers?partner_id=${partnerId}`,
      },
      'get-customer': {
        method: 'GET',
        endpoint: `${base}/api/v1/customers/{customer_id}`,
        fullUrl: `${base}/api/v1/customers/${getCustomerId}`,
      },
    };
  }, [base, getCustomersPartnerId, getCustomerId, partners, updateCustomerId]);

  const currentFullUrl = tabConfig[activeTab]?.fullUrl || '';

  const copyToClipboard = useCallback((text) => {
    navigator.clipboard.writeText(text);
  }, []);

  const selectedPartner = useMemo(
    () => partners.find((p) => String(p.id) === String(selectedPartnerIdFromJson)),
    [partners, selectedPartnerIdFromJson]
  );

  const ensurePartnerInJson = (raw, partnerId) => {
    try {
      const obj = typeof raw === 'string' ? JSON.parse(raw) : raw;
      const pid = partnerId != null ? partnerId : (partners[0] && partners[0].id);
      return { ...obj, partner_id: pid };
    } catch {
      return { partner_id: partners[0] && partners[0].id };
    }
  };

  const getPartnerNameFromPayload = (payload) => {
    if (!payload || payload.partner_id == null) return selectedPartner ? selectedPartner.name : 'Unknown';
    const p = partners.find((x) => String(x.id) === String(payload.partner_id));
    return p ? p.name : 'Unknown';
  };

  const handleSend = async () => {
    setError('');
    setIsLoading(true);
    let partnerName = partners[0] ? partners[0].name : 'Connection';
    try {
      let payload = {};
      let actionLabel = '';
      let method = tabConfig[activeTab]?.method || 'GET';
      let url = tabConfig[activeTab]?.fullUrl || '';

      if (activeTab === 'create') {
        payload = ensurePartnerInJson(createJson, selectedPartnerIdFromJson);
        partnerName = getPartnerNameFromPayload(payload);
        actionLabel = 'Create Customer API';
        const r = await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        const res = await r.json().catch(() => ({}));
        const responseStr = JSON.stringify(res, null, 2);
        setResponseText(responseStr);
        if (onLogEntry && partnerName) {
          onLogEntry(partnerName, { action: actionLabel, status: r.ok ? 'Success' : 'Failed', details: responseStr.slice(0, 80) + '...', amount: 0 });
        }
        setIsLoading(false);
        return;
      }
      if (activeTab === 'update') {
        const u = JSON.parse(updateJson);
        payload = ensurePartnerInJson(updateJson, u.partner_id != null ? String(u.partner_id) : (partners[0] && String(partners[0].id)));
        partnerName = getPartnerNameFromPayload(payload);
        actionLabel = 'Update Customer API';
        const r = await fetch(url, { method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
        const res = await r.json().catch(() => ({}));
        setResponseText(JSON.stringify(res, null, 2));
        if (onLogEntry && partnerName) {
          onLogEntry(partnerName, { action: actionLabel, status: r.ok ? 'Success' : 'Failed', details: String(res.error || 'OK'), amount: 0 });
        }
        setIsLoading(false);
        return;
      }
      if (activeTab === 'get-customers') {
        actionLabel = 'Get Customers API';
        const r = await fetch(url);
        const res = await r.json().catch(() => ({}));
        setResponseText(JSON.stringify(res, null, 2));
        if (onLogEntry && partnerName) {
          onLogEntry(partnerName, { action: actionLabel, status: r.ok ? 'Success' : 'Failed', details: '', amount: 0 });
        }
        setIsLoading(false);
        return;
      }
      if (activeTab === 'get-customer') {
        partnerName = partners[0] ? partners[0].name : 'Connection';
        actionLabel = 'Get Customer API';
        const r = await fetch(url);
        const res = await r.json().catch(() => ({}));
        setResponseText(JSON.stringify(res, null, 2));
        if (onLogEntry && partnerName) {
          onLogEntry(partnerName, { action: actionLabel, status: r.ok ? 'Success' : 'Failed', details: '', amount: 0 });
        }
        setIsLoading(false);
        return;
      }
    } catch (e) {
      setError(e.message || 'Request failed');
      setResponseText('');
      if (onLogEntry && partnerName) {
        onLogEntry(partnerName, { action: 'Customer API', status: 'Failed', details: String(e.message), amount: 0 });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClear = () => {
    if (activeTab === 'create') {
      setCreateJson(JSON.stringify(getDefaultCreateFields(partners), null, 2));
    } else if (activeTab === 'update') {
      setUpdateJson(JSON.stringify(getDefaultUpdateFields(partners), null, 2));
    }
    setResponseText('');
    setError('');
  };

  const requestSections = {
    create: {
      title: 'Request body',
      content: (
        <textarea
          className="api-textarea"
          value={createJson}
          onChange={(e) => setCreateJson(e.target.value)}
          rows={10}
          placeholder='{ "partner_id": 1, "name": "...", ... }'
        />
      ),
      showActions: true,
    },
    update: {
      title: 'Request body',
      content: (
        <textarea
          className="api-textarea"
          value={updateJson}
          onChange={(e) => setUpdateJson(e.target.value)}
          rows={10}
          placeholder='{ "partner_id": 1, "customer_id": "CZE000001", ... }'
        />
      ),
      showActions: true,
    },
    'get-customers': {
      title: 'Query parameters',
      content: (
        <div className="api-form">
          <div className="form-group">
            <label>partner_id (required)</label>
            <select
              className="form-input"
              value={getCustomersPartnerId}
              onChange={(e) => setGetCustomersPartnerId(e.target.value)}
            >
              <option value="">Select connection</option>
              {partners.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>
          <pre className="api-pre">{`page (optional) - Page number, default 1\nper_page (optional) - Items per page, default 20`}</pre>
        </div>
      ),
      showActions: true,
    },
    'get-customer': {
      title: 'Path parameter',
      content: (
        <div className="api-form">
          <div className="form-group">
            <label>customer_id</label>
            <input
              type="text"
              className="form-input"
              value={getCustomerId}
              onChange={(e) => setGetCustomerId(e.target.value)}
              placeholder="CZE000001"
            />
          </div>
        </div>
      ),
      showActions: true,
    },
  };

  const responseSections = {
    create: JSON.stringify({
      status: 'success',
      code: '200',
      message: 'Customer created successfully',
      data: {
        customer_id: 'CZE000003',
        name: 'John Doe',
        created_at: '2026-02-10T10:30:00Z',
      },
      request_id: 'req_123456789',
    }, null, 2),
    update: JSON.stringify({
      status: 'success',
      code: '200',
      message: 'Customer updated successfully',
      data: { customer_id: 'CZE000001', name: 'John Doe' },
      request_id: 'req_123456789',
    }, null, 2),
    'get-customers': JSON.stringify({
      status: 'success',
      code: '200',
      data: [
        { customer_id: 'CZE000001', name: 'John Doe', created_at: '2026-02-01' },
        { customer_id: 'CZE000002', name: 'Jane Smith', created_at: '2026-02-05' },
      ],
      request_id: 'req_123456789',
    }, null, 2),
    'get-customer': JSON.stringify({
      status: 'success',
      code: '200',
      data: {
        customer_id: 'CZE000001',
        name: 'John Doe',
        partner_id: 1,
        created_at: '2026-02-01',
      },
      request_id: 'req_123456789',
    }, null, 2),
  };

  const errorSections = {
    create: [
      { code: '400', description: 'Bad Request – Invalid or missing required fields' },
      { code: '401', description: 'Unauthorized – Invalid or missing API key' },
      { code: '409', description: 'Conflict – Customer already exists for this partner' },
      { code: '422', description: 'Unprocessable Entity – Validation failed' },
      { code: '500', description: 'Internal Server Error' },
    ],
    update: [
      { code: '400', description: 'Bad Request – Invalid request body' },
      { code: '401', description: 'Unauthorized – Invalid or missing API key' },
      { code: '404', description: 'Not Found – Customer not found' },
      { code: '422', description: 'Unprocessable Entity – Validation failed' },
      { code: '500', description: 'Internal Server Error' },
    ],
    'get-customers': [
      { code: '401', description: 'Unauthorized – Invalid or missing API key' },
      { code: '403', description: 'Forbidden – No access to this partner' },
      { code: '500', description: 'Internal Server Error' },
    ],
    'get-customer': [
      { code: '401', description: 'Unauthorized – Invalid or missing API key' },
      { code: '404', description: 'Not Found – Customer not found' },
      { code: '500', description: 'Internal Server Error' },
    ],
  };

  const reqSection = requestSections[activeTab];
  const resSection = responseSections[activeTab];
  const errSection = errorSections[activeTab];

  return (
    <div className="api-explorer api-explorer-sidebar">
      <div className="api-explorer-header">
        <h3>API Explorer</h3>
        <button type="button" className="api-explorer-close" onClick={onClose} aria-label="Close">×</button>
      </div>

      <div className="api-explorer-tabs">
        {API_TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={`api-tab ${activeTab === tab.id ? 'active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="api-explorer-panel">
        <div className="api-endpoint-block">
          <label className="api-endpoint-label">Base API endpoint</label>
          <div className="api-endpoint-value">{baseUrl}</div>
          <label className="api-endpoint-label">API endpoint</label>
          <div className="api-endpoint-row">
            <input type="text" className="api-endpoint-input" value={currentFullUrl} readOnly />
            <button
              type="button"
              className="api-endpoint-copy"
              onClick={() => copyToClipboard(currentFullUrl)}
              title="Copy endpoint"
              aria-label="Copy endpoint"
            >
              <CopyIcon />
            </button>
          </div>
          <div className="api-method-badge-inline">
            <span className="api-method-badge">{tabConfig[activeTab]?.method || 'GET'}</span>
          </div>
        </div>

        {reqSection && (
          <section className="api-doc-section">
            <h4>{reqSection.title}</h4>
            {reqSection.content}
            {reqSection.showActions && (
              <div className="api-tab-actions">
                <button type="button" className="btn btn-primary btn-sm" onClick={handleSend} disabled={isLoading}>
                  {isLoading ? 'Sending...' : 'Send'}
                </button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={handleClear}>Clear</button>
                <button type="button" className="btn btn-secondary btn-sm" onClick={onClose}>Cancel</button>
              </div>
            )}
          </section>
        )}

        <section className="api-doc-section">
          <h4>Response</h4>
          <pre className="api-pre api-response-pre">{resSection}</pre>
        </section>

        <section className="api-doc-section">
          <h4>Error</h4>
          <table className="doc-table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Description</th>
              </tr>
            </thead>
            <tbody>
              {errSection.map((row) => (
                <tr key={row.code}>
                  <td><code>{row.code}</code></td>
                  <td>{row.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      {(reqSection?.showActions && (activeTab === 'create' || activeTab === 'update' || activeTab === 'get-customers' || activeTab === 'get-customer')) && (
        <div className="api-response-section">
          <h4>Live response</h4>
          {error && <div className="error-message">{error}</div>}
          <textarea
            className="api-textarea response-textarea"
            readOnly
            value={responseText || (isLoading ? 'Loading...' : 'Click Send to see response')}
            rows={6}
          />
        </div>
      )}
    </div>
  );
};

export default ApiExplorer;
